// Wrapper para las vistas fullScreen de los charts.
// Layout estático que ocupa exactamente el viewport, sin scroll vertical.
// Toolbar compacto superior con título + reglas + cerrar.
// Providers: reglas verticales, comentarios, descarga.

import {
  Box, IconButton, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ChartRulerProvider } from '../../../context/ChartRulerContext';
import { ChartExceptionProvider } from '../../../context/ChartExceptionContext';
import { DashboardDownloadProvider } from '../../../context/DashboardDownloadContext';
import Page from '../../Layout/Page';
import ChartRulerButton from '../../../views/ClientDetail/Layout/SectorDetail/component/ChartRulerButton';
import { CHART_COLORS } from '../chartColors';

const TOOLBAR_HEIGHT = 56;
// Reserva para: header del chart (título + acciones) + card padding + bordes
const CHART_RESERVE = 130;

interface FullScreenChartShellProps {
  title?: string;
  children: (height: number) => React.ReactNode;
}

const FullScreenChartShell: React.FC<FullScreenChartShellProps> = ({
  title = 'Gráfico',
  children,
}) => {
  const history = useHistory();
  const [chartHeight, setChartHeight] = useState<number>(
    Math.max(300, window.innerHeight - TOOLBAR_HEIGHT - CHART_RESERVE),
  );
  useEffect(() => {
    const onResize = () => {
      setChartHeight(Math.max(300, window.innerHeight - TOOLBAR_HEIGHT - CHART_RESERVE));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return (
    <Page title={title}>
      <ChartExceptionProvider>
        <ChartRulerProvider>
          <DashboardDownloadProvider>
            <Box
              sx={{
                height: '100vh',
                width: '100vw',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: CHART_COLORS.bg,
              }}
            >
              {/* Toolbar */}
              <Box
                sx={{
                  height: TOOLBAR_HEIGHT,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: CHART_COLORS.surface,
                  borderBottom: `1px solid ${CHART_COLORS.border}`,
                  px: 2,
                  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: CHART_COLORS.textPrimary,
                    fontSize: '1rem',
                    letterSpacing: '-0.01em',
                  }}
                >
                  {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChartRulerButton />
                  <Tooltip title="Salir de pantalla completa">
                    <IconButton
                      onClick={() => history.goBack()}
                      size="small"
                      sx={{
                        color: CHART_COLORS.textMuted,
                        '&:hover': { background: CHART_COLORS.overlay, color: CHART_COLORS.textPrimary },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Chart area */}
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  '& > div, & > .MuiCard-root': {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '0 !important',
                  },
                }}
              >
                {children(chartHeight)}
              </Box>
            </Box>
          </DashboardDownloadProvider>
        </ChartRulerProvider>
      </ChartExceptionProvider>
    </Page>
  );
};

export default FullScreenChartShell;
